import { observer, inject } from "mobx-react";
import { reaction } from "mobx";
import React, { useEffect, useMemo, useState } from "react";
import { Utils } from "../../common/utils";
import { hasCommitteesFilter, hasCoursesFilter, hasMenteesFilter, isUserStatusFilter, isYesNoAnyFilter } from "../../common/filters";

import { UserStore, CommonStore } from "../../stores";
import { LicenseInfo, Mapping } from "Stores/commonStore";
import { User, UsersDataQuery } from "Stores/userStore";
import { AppStore } from "Stores/appStore";
import { SchoolStore } from "Stores/superStore/schools";
import { Defaults } from "../../common/utils";

import { UserMappingSelector } from "./MappingSelector";
import { Alert, Button, Col, OverlayTrigger, Tooltip, Spinner as BSSpinner, Dropdown } from "react-bootstrap";

import CreateUserDrawer from "./CreateUserDrawer";
import { UserFormModal } from "./formModal";

import { CellContext, ColumnDef, ColumnFilter, ExpandedState, Row as RowInfo } from "@tanstack/react-table";
import { StyledTable } from "Components/Common/StyledTable";
import DebouncedInput from "Components/Common/DebouncedInput";
import { CaretDownIcon } from "Components/Icons/CaretDown";
import { CaretRightIcon } from "Components/Icons/CaretRight";
import { CheckIcon } from "Components/Icons/CheckIcon";
import { CheckCircleIcon } from "Components/Icons/CheckCircleIcon";
import { DeleteIcon } from "Components/Icons/DeleteIcon";
import { CircleAlertIcon } from "Components/Icons/CircleAlertIcon";
import CustomToggle from "Components/Common/CustomToggle";
import PageTitle from "Components/Common/PageTitle";

interface StartMasqueradeButtonProps {
    email: string;
    appStore?: AppStore;
}

interface StartMasqueradeButtonState {
    isWaiting: boolean;
}

@inject("appStore")
@observer
class StartMasqueradeButton extends React.Component<StartMasqueradeButtonProps, StartMasqueradeButtonState> {
    state = {
        isWaiting: false,
    };

    handleClick = () => {
        this.setState({ isWaiting: true }, () => {
            this.props.appStore!.startMasquerade(this.props.email);
        });
    };

    render() {
        return (
            <Button disabled={Utils.getIsMasquerading() || this.props.appStore!.isChangingMasquerade || this.state.isWaiting} onClick={this.handleClick} variant="secondary" size="sm">
                {this.state.isWaiting ? <BSSpinner animation="border" size="sm" /> : "Log In As"}
            </Button>
        );
    }
}

interface UserManagementContainerState {
    showUserModal: boolean;
    currentUser?: User;
    showCommittees: boolean | string;
    showModal?: Mapping;
    selectedFaculty: User | undefined;
    pageSize: number;
    pageNumber: number;
    expand?: { [index: number]: boolean };
    showFilters: boolean;
}

export interface UserManagementProps {
    userStore?: UserStore;
    appStore?: AppStore;
    commonStore?: CommonStore;
    schoolStore?: SchoolStore;
}

@inject("userStore", "appStore", "commonStore", "schoolStore")
@observer
export class UserManagement extends React.Component<UserManagementProps, UserManagementContainerState> {
    defaultPage = Defaults.tablePage();
    defaultPageSize = Defaults.tablePageSize();

    state = {
        showUserModal: false,
        currentUser: undefined,
        showCommittees: false,
        showMenteeModal: undefined,
        showModal: undefined,
        selectedFaculty: undefined,
        expand: undefined,
        pageNumber: this.defaultPage,
        pageSize: this.defaultPageSize,
        showFilters: false,
    };

    componentDidMount() {
        Utils.setViewName("UserList", "Users");
    }

    render() {
        if (!this.props.userStore || !this.props.schoolStore || !this.props.appStore || !this.props.commonStore) {
            return null;
        }
        const defaultFilters: ColumnFilter[] = [];

        if (Utils.isSuperAdmin()) {
            defaultFilters.push({
                id: "is_superadmin",
                value: "no",
            });
        }

        return <UserManagementPage userStore={this.props.userStore} commonStore={this.props.commonStore} schoolStore={this.props.schoolStore} appStore={this.props.appStore} defaultFilters={defaultFilters} />;
    }

    autoClose = () => {
        this.setState({ expand: {} });
    };
}

interface UserManagementPageProps {
    userStore: UserStore;
    commonStore: CommonStore;
    schoolStore: SchoolStore;
    appStore: AppStore;
    defaultFilters: ColumnFilter[];
}

enum UserStatus {
    Pending = "pending",
    Active = "active",
    Inactive = "inactive",
}

type StatusIcons = {
    [key in UserStatus]: JSX.Element;
};

function UserManagementPage({ userStore, commonStore, schoolStore, appStore }: UserManagementPageProps) {
    if (!Utils.hasAdminAccess()) {
        return (
            <Col sm={12} md={12}>
                <Alert variant={"warning"}>Sorry, you don&apos;t have access to user management. If you think this is an error, please contact your administrator so he/she can give you the correct access level.</Alert>
            </Col>
        );
    }

    const [licenseInfo, setLicenseInfo] = useState<LicenseInfo | null>(null);
    const [domainWhitelist, setDomainWhitelist] = useState<string | null>(null);
    const [showUserModal, setShowUserModal] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [selectedFaculty, setSelectedFaculty] = useState<User | null>(null);
    const [showModal, setShowModal] = useState<Mapping | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [dataQuery, setDataQuery] = useState<UsersDataQuery | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [pageSize, setPageSize] = useState(50);
    const [filters, setFilters] = useState<Filters>({
        internal: "No",
        status: "Any",
        admin: "Any",
        mentees: "Any",
    });

    const columns = useMemo<ColumnDef<User>[]>(
        () => [
            {
                id: "expander",
                header: () => null,
                cell: ({ row }) => <div>{row.getCanExpand() ? <span onClick={row.getToggleExpandedHandler()}>{row.getIsExpanded() ? <CaretDownIcon size="20" className="sub-section-caret" /> : <CaretRightIcon size="20" className="sub-section-caret" />}</span> : null}</div>,
            },
            {
                header: "Verified",
                accessorKey: "verified",
                cell: (props: CellContext<User, string>) => {
                    const user = props.row.original;

                    if (props.getValue()) {
                        return <CheckIcon className="check-icon" />;
                    } else if (user.status === "pending") {
                        const resentInviteButton = (
                            <Button size="sm" className="btn-tertiary btn-icon" data-test-id={`user-resend-invite-${user.ruuid}`} onClick={() => resendInvite(user)}>
                                Resend Invite
                            </Button>
                        );

                        if (Utils.isSuperAdmin()) {
                            return (
                                <div className="d-flex align-items-center">
                                    {resentInviteButton}
                                </div>
                            );
                        }

                        return (
                            <div className="d-flex align-items-center">
                                {resentInviteButton}
                            </div>
                        );
                    } else {
                        return null;
                    }
                },
                size: 100,
            },
            {
                header: "Status",
                accessorKey: "status",
                filterFn: isUserStatusFilter,
                cell: (props) => {
                    const user = props.row.original as User;
                    const status = props.getValue() as UserStatus;

                    const iconMap: StatusIcons = {
                        [UserStatus.Pending]: <CircleAlertIcon className="alertIcon" />,
                        [UserStatus.Active]: <CheckCircleIcon className="checkIcon" />,
                        [UserStatus.Inactive]: <></>,
                    };

                    return (
                        <div className="table-icon-cell icon-cell-padding">
                            <OverlayTrigger overlay={<Tooltip id={`tooltip-${user.ruuid}`}>{status}</Tooltip>}>{iconMap[status]}</OverlayTrigger>
                        </div>
                    );
                },
                size: 90,
            },
            {
                header: "Prefix",
                accessorKey: "prefix",
                size: 70,
            },
            {
                header: "First Name",
                accessorKey: "first_name",
                size: 250,
            },
            {
                header: "Last Name",
                accessorKey: "last_name",
                size: 250,
            },
            {
                header: "Suffix",
                accessorKey: "suffix",
                size: 70,
            },
            ...(Utils.isSuperAdmin()
                ? ([
                    {
                        id: "sign_in",
                        header: () => null,
                        cell: (props) => {
                            const currentUserEmail = Utils.getCurrentUser()!.email;
                            const rowUser = props.row.original;
                              // Don't allow masquerading as current user or as users that are not active
                            if (rowUser.email === currentUserEmail || rowUser.status?.toLowerCase() !== "active") {
                                return <div></div>;
                            }
                            return <StartMasqueradeButton email={props.row.original.email} />;
                        },
                    },
                ] as ColumnDef<User>[])
                : []),
            {
                header: "Email",
                accessorKey: "email",
                                cell: (props) => {
                    const email = props.getValue() as string;
                    if (email && email.length > 16) {
                        return <div style={{overflowWrap: "break-word", wordWrap: "break-word", maxWidth: "160px"}}>{email}</div>;
                    }
                    return email;
                },
                size: 220,
            },
            {
                header: "Admin",
                accessorKey: "is_admin",
                filterFn: isYesNoAnyFilter,
                cell: (props) => {
                    const isAdmin = props.getValue();
                    const isKeyAdmin = props.row.original.is_key_admin;
                    if (isAdmin && isKeyAdmin) {
                        return (
                            <div className="icon-cell-padding">
                                <i className="table-icon-cell icon icon-star" />
                            </div>
                        );
                    } else if (isAdmin) {
                        return <CheckCircleIcon className="checkIcon" />;
                    }
                    return <span></span>;
                },
                size: 80,
            },
            ...(Utils.isSuperAdmin()
                ? [
                    {
                        header: "Internal",
                        id: "is_superadmin",
                        accessorKey: "is_superadmin",
                        filterFn: isYesNoAnyFilter,
                        cell: (props) =>
                            props.getValue() ? (
                                <div className="icon-cell-padding">
                                    <i className="table-icon-cell icon icon-star" />
                                </div>
                            ) : (
                                ""
                            ),
                        minSize: 60,
                        maxSize: 80,
                    },
                ]
                : []),
            {
                header: "Mentees",
                accessorKey: "student_count",
                filterFn: hasMenteesFilter,
                cell: (props) => {
                    const user = props.row.original;
                    return renderMappingCell("students", user, userStore.isUpdatingUserById, userStore.isUpdatingMappingType === "students");
                },
                size: 130,
            },
            {
                header: "Courses",
                accessorKey: "course_count",
                filterFn: hasCoursesFilter,
                cell: (props) => {
                    const user = props.row.original;
                    return renderMappingCell("courses", user, userStore.isUpdatingUserById, userStore.isUpdatingMappingType === "courses");
                },
                size: 130,
            },
            {
                header: "Committees",
                accessorKey: "committee_count",
                filterFn: hasCommitteesFilter,
                cell: (props) => {
                    const user = props.row.original as User;
                    return renderMappingCell("committees", user, userStore.isUpdatingUserById, userStore.isUpdatingMappingType === "committees");
                },
                size: 130,
            },
            {
                id: "deleter",
                header: () => null,
                cell: (props) => {
                    const user = props.row.original as User;
                    if (user.is_key_admin && !Utils.isSuperAdmin()) {
                        return null;
                    }
                    return (
                        <div>
                            <Button
                                id={`user-table-edit-delete-${user.ruuid}`}
                                onClick={() => {
                                    deleteUser(user);
                                }}
                                variant="icon"
                            >
                                <DeleteIcon className="delete-icon" />
                            </Button>
                        </div>
                    );
                },
            },
        ],
        []
    );

    useEffect(() => {
        async function fetchLicenseInfo() {
            await commonStore.fetchLicenseInfo();
            setLicenseInfo({ ...commonStore.licenseInfo });
        }
        fetchLicenseInfo().catch(() => {});
    }, [refreshKey]);

    useEffect(() => {
        async function fetchDomainWhitelist() {
            await schoolStore.fetchDomainWhitelist();
            setDomainWhitelist(schoolStore.domainWhitelist);
        }

        fetchDomainWhitelist().catch(() => {});
    }, []);

    useEffect(() => {
        reaction(
            () => userStore.users,
            (newUsers) => {
                setDataQuery({
                    data: newUsers,
                    result: { totalCount: newUsers.length },
                });
            }
        );
    }, []);

    useEffect(() => {
        async function fetchUsers() {
            await userStore.fetchUserList();
            setInitialLoading(false);
        }

        fetchUsers().catch(() => {});
    }, [refreshKey]);

    const applyFilters = (data: User[]): User[] => {
        return data.filter((user) => {
            // Filter Internal
            if (filters.internal !== "Any") {
                const isInternal = user.is_superadmin;
                if (filters.internal === "Yes" && !isInternal) return false;
                if (filters.internal === "No" && isInternal) return false;
            }

            // Filter Status
            if (filters.status !== "Any") {
                if (user.status?.toLowerCase() !== filters.status?.toLowerCase()) {
                    return false;
                }
            }

            // Filter Admin
            if (filters.admin !== "Any") {
                const isAdmin = user.is_admin;
                if (filters.admin === "Yes" && !isAdmin) return false;
                if (filters.admin === "No" && isAdmin) return false;
            }

            // Filter Mentees
            if (filters.mentees !== "Any") {
                const hasMentees = user.student_count > 0;
                if (filters.mentees === "Has Mentees" && !hasMentees) return false;
                if (filters.mentees === "No Mentees" && hasMentees) return false;
            }

            // Filter Search
            if (searchTerm) {
                const term = searchTerm?.toLowerCase();
                const matches = user.prefix?.toLowerCase().includes(term) || user.first_name?.toLowerCase().includes(term) || user.last_name?.toLowerCase().includes(term) || user.email?.toLowerCase().includes(term) || user.course_count?.toString().includes(term) || user.committee_count?.toString().includes(term);
                if (!matches) return false;
            }

            return true;
        });
    };

    const filteredData = useMemo(() => {
        if (!dataQuery) return [];
        return applyFilters(dataQuery.data);
    }, [dataQuery, filters, searchTerm]);

    const resendInvite = (user: User) => {
        userStore.resendInviteToUser(user);
        setRefreshKey((prevState) => prevState + 1);
    };

    const closeModal = () => {
        setShowUserModal(false);
    };

    const deleteUser = (user: User) => {
        appStore.showConfirmModal({
            element: <span>{`Are you sure you want to delete ${user.name}?`}</span>,
            accept: () => {
                userStore.deleteUser(user.ruuid).then(() => {
                    setRefreshKey((prevState) => prevState + 1);
                });
            },
            showActions: true,
        });
    };

    const handleSubmitForm = () => {
        setShowUserModal(false);
        setRefreshKey((prevState) => prevState + 1);
    };

    const showMap = async (faculty: User, type: Mapping) => {
        const selectedFaculty = await userStore.fetchUserById(faculty.ruuid);
        setSelectedFaculty(selectedFaculty);
        setShowModal(type);
    };

    const renderMappingCell = (type: Mapping, user: User, updatingId?: string, isUpdatingType?: boolean) => {
        if (type === "students" || type === "courses" || type === "committees") {
            let count = 0;
            if (type === "students") count = user.student_count;
            if (type === "courses") count = user.course_count;
            if (type === "committees") count = user.committee_count;
            let countDisplay = <span>[{count}]</span>;
            if (updatingId && user.ruuid === updatingId && isUpdatingType) {
                countDisplay = <BSSpinner size={"sm"} variant={"secondary"} animation={"border"} className="green-spinner tiny list-spinner" />;
            }
            return (
                <div className="icon-cell-padding">
                    {countDisplay}
                    <Button
                        id={`user-table-edit-${type}-${user.ruuid}`}
                        data-test-id={`user-table-edit-${type}-${user.ruuid}`}
                        variant={"icon"}
                        disabled={userStore.isUpdatingUserById !== undefined}
                        onClick={() => {
                            showMap(user, type);
                        }}
                    >
                        <i className="icon icon-pencil-square-o" aria-hidden="true" />
                    </Button>
                </div>
            );
        } else {
            return <span></span>;
        }
    };

    const hideMap = () => {
        userStore.resetAddedMapCount();
        userStore.resetRemovedMapCount();
        setSelectedFaculty(null);
        setShowModal(null);
    };

    const renderSubComponent = (row: RowInfo<User>) => {
        return (
            <UserFormModal
                handleCloseModal={() => {
                    row.toggleExpanded();
                }}
                handleSubmitUser={() => {
                    row.toggleExpanded();
                    handleSubmitForm();
                }}
                currentUser={row.original}
            />
        );
    };

    const renderList = () => {
        if (initialLoading) {
            return (
                <div className={"text-center"}>
                    <BSSpinner animation={"border"} variant={"secondary"} />
                </div>
            );
        }

        if (!filteredData || filteredData.length === 0) {
            return <Alert variant={"info"}>No users available</Alert>;
        }

        return (
            <>
                <StyledTable
                    columns={columns}
                    defaultData={filteredData}
                    expanded={expanded}
                    setExpanded={setExpanded}
                    noDataMessage={"No users available"}
                    isLoading={initialLoading}
                    className="user-table"
                    getRowCanExpand={() => true}
                    onIsExpanded={(row) => renderSubComponent(row)}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                />
                {showModal !== null && selectedFaculty !== null ? <UserMappingSelector mapType={showModal} faculty={selectedFaculty!} handleClose={hideMap} /> : null}
            </>
        );
    };

    return (
        <>
            <PageTitle pageTitle="Users" />
            <div className="card-layout">
                <div className="card-header">
                    <div className="title">
                        <h3>Users</h3>
                        <span>{licenseInfo && `${licenseInfo.current_users} of ${licenseInfo.max_number_users} user licenses used`}</span>
                    </div>
                    <div className="actions" style={{ display: "flex", gap: "10px" }}>
                        <Button id="enflux-users-create-user-button" data-test-id="users-create-user" onClick={() => setShowUserModal(true)}>create user</Button>
                    </div>
                </div>
                <div className="search-container">
                    <div className="search-bar">
                        <DebouncedInput type="text" value={searchTerm} onChange={(value) => setSearchTerm(value as string)} placeholder="Search by Prefix, First Name, Last Name, Email, Course or Committee name" className="search-input" data-test-id="users-search-input" />
                        <i className={"type icon icon-search search-icon-bar"} aria-hidden="true" data-testid="icon-search"></i>
                    </div>
                    {Utils.isSuperAdmin() && (
                        <Button
                            id="enflux-users-filter-button"
                            data-test-id="users-filter-button"
                            disabled={false}
                            title="Filter"
                            className={"btn-icon"}
                            onClick={() => {
                                setShowFilters(!showFilters);
                            }}
                        >
                            <i className="icon icon-filter" />
                        </Button>
                    )}
                </div>
                {showFilters && <AdvancedFilters onFilterChange={setFilters} filters={filters} />}
                {renderList()}
                {showUserModal && <CreateUserDrawer handleCloseModal={closeModal} handleSubmitUser={handleSubmitForm} domainWhitelist={domainWhitelist} />}
            </div>
        </>
    );
}

type Filters = {
    internal: string;
    status: string;
    admin: string;
    mentees: string;
};

interface AdvancedFiltersProps {
    onFilterChange: (updatedFilters: Filters) => void;
    filters: Filters;
}

const AdvancedFilters = ({ onFilterChange, filters }: AdvancedFiltersProps) => {
    const handleFilterChange = (filterKey: string, value: string) => {
        onFilterChange({ ...filters, [filterKey]: value });
    };

    return (
        <div className="filter-user-container">
            <span>Filters</span>
            <div className="filter-input-container">
                <Dropdown className="filter-dropdown">
                    <Dropdown.Toggle variant="secondary" id="dropdown-internal" as={CustomToggle}>
                        Internal: {filters.internal}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {["Any", "Yes", "No"].map((option) => (
                            <Dropdown.Item key={option} onClick={() => handleFilterChange("internal", option)}>
                                {option}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
                <Dropdown className="filter-dropdown">
                    <Dropdown.Toggle variant="secondary" id="dropdown-status" as={CustomToggle}>
                        Status: {filters.status}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {["Active", "Inactive", "Pending"].map((option) => (
                            <Dropdown.Item key={option} onClick={() => handleFilterChange("status", option)}>
                                {option}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
                <Dropdown className="filter-dropdown">
                    <Dropdown.Toggle variant="secondary" id="dropdown-admin" as={CustomToggle}>
                        Admin: {filters.admin}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {["Any", "Yes", "No"].map((option) => (
                            <Dropdown.Item key={option} onClick={() => handleFilterChange("admin", option)}>
                                {option}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
                <Dropdown className="filter-dropdown">
                    <Dropdown.Toggle variant="secondary" id="dropdown-mentees" as={CustomToggle}>
                        Mentees: {filters.mentees}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {["Any", "Has Mentees", "No Mentees"].map((option) => (
                            <Dropdown.Item key={option} onClick={() => handleFilterChange("mentees", option)}>
                                {option}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        </div>
    );
};

